<script lang="ts" setup>
import type { ApalumaChatMessage, ChatMessageFeedback } from '@/types/api-response/chat-session-types.ts';
import { Icon } from '@iconify/vue';
import DOMPurify from 'dompurify';
import { computed, useTemplateRef, watch } from 'vue';
import { IconLib } from '@/assets/icon-library.ts';
import ChatMessageFeedbackPanel from '@/components/home-page/chat/chat-history/chat-message/chat-message-feedback.vue';
import ChatMessageToolbar from '@/components/home-page/chat/chat-history/chat-message/chat-message-toolbar.vue';
import LumaThoughtTrail from '@/components/home-page/chat/chat-history/chat-message/luma-thought-trail.vue';
import { useLDFlag } from '@/composables/use-ld-flag.ts';
import { useLumaAnswerActions } from '@/composables/use-luma-answer-actions';
import { useScreenWidth } from '@/composables/use-screen-width.ts';
import { useChatSessionStore } from '@/stores/chat-session-store.ts';
import { useNmedDocSearchFilterStore } from '@/stores/nmed/nmed-doc-search-filter-store.ts';
import { referenceDocumentCount } from '@/utils/luma-references';

const props = defineProps<{
   message: ApalumaChatMessage;
   originalQuestion?: string;
}>();

const emit = defineEmits<{
   selectFollowupQuestion: [question: string];
   retryMessage: [messageId: number];
}>();

const showFollowUpQuestions = useLDFlag('feature-luma-follow-up-questions', false);

const chatSessionStore = useChatSessionStore();
const { stopMessage, setMessageFeedback } = chatSessionStore;
const nmedDocSearchFilterStore = useNmedDocSearchFilterStore();

const { isSmScreen } = useScreenWidth();

const {
   isOpeningSearch,
   searchResultCount,
   showReferences,
   openSuggestedSearch,
   loadSuggestedSearchCount,
} = useLumaAnswerActions();

// Toolbar ref to access feedback state
const toolbarRef = useTemplateRef<InstanceType<typeof ChatMessageToolbar>>('toolbarRef');

const isError = computed(() => props.message.error);

/**
 * Whether this answer's references are the ones currently in the document table.
 */
const isDocsAdded = computed(() => {
   return props.message.id.toString() === nmedDocSearchFilterStore.lumaDocsChatId;
});

/**
 * Distinct documents behind the references — what the table will actually list,
 * which is far fewer than `reference.length` when an answer cites several pages
 * of the same document.
 */
const referenceCount = computed(() => referenceDocumentCount(props.message.reference));

/** Whether there is a suggested search worth counting. */
const hasSuggestedSearch = computed(() => Boolean(props.message.filter?.length));

/**
 * An action appears only once it is known to lead somewhere.
 *
 * The two decorations are independent — nearly every answer has both — but
 * either can be empty, and Luma's suggested searches frequently match nothing
 * at all. A button offering zero documents is worse than no button, and one
 * that appears while counting only to vanish at zero is worse still, so the
 * search action stays hidden until a non-zero total comes back. An unknown
 * count (the search failed) is treated as nothing to offer.
 */
const hasReferences = computed(() => referenceCount.value > 0);
const hasSearchResults = computed(() => (searchResultCount.value ?? 0) > 0);

const isStopped = computed(() => props.message.stopped);
const isLoading = computed(() => {
   return props.message.role === 'assistant' && props.message.status === 'processing' && !props.message.error && !props.message.stopped;
});

/**
 * Luma's reasoning trail for this turn — empty on reloaded threads, since
 * thoughts are session-only.
 */
const thoughts = computed(() => props.message.thoughts ?? []);

/** Show the settled trail alongside a finished answer, when we have one. */
const hasThoughtTrail = computed(() => thoughts.value.length > 0);

/**
 * The final answer: the one thing feedback applies to.
 *
 * Not `status === 'done'` — assistant messages are persisted without a status,
 * so anything loaded from a thread comes back with `status: null`. This asks
 * the question the other way round: has this message finished, unhappily or
 * otherwise? A processing, errored, stopped or cancelled turn is never final,
 * and neither is a streaming thought — those aren't messages at all.
 */
const isFinalAnswer = computed(() =>
   props.message.role === 'assistant'
   && Boolean(props.message.content)
   && props.message.status !== 'processing'
   && props.message.status !== 'error'
   && props.message.status !== 'stopped'
   && !props.message.error
   && !props.message.stopped,
);
/**
 * Count the suggested search up front so its size is visible before the click.
 * Only for a settled answer: a streaming turn's filters aren't final yet.
 */
watch(
   () => isFinalAnswer.value && hasSuggestedSearch.value,
   (ready) => {
      if (ready)
         loadSuggestedSearchCount(props.message);
   },
   { immediate: true },
);

const sanitizedContent = computed(() => {
   if (!props.message.content)
      return '';
   return DOMPurify.sanitize(props.message.content);
});

/**
 * What the live row says before Luma's first progress event.
 *
 * This used to cycle through invented activities — 'thinking', 'reading',
 * 'Checking Security', 'analyzing' — every three seconds, which described work
 * nobody had done: nothing has come back from Luma yet at this point, and on a
 * slow first turn the reader watched several of those claims go by before the
 * real trail started. The trail says what Luma actually did the moment it says
 * anything; until then, one honest line.
 */
const LOADING_FALLBACK = 'Asking Luma';

/**
 * Stops the message
 */
function handleStop() {
   stopMessage(props.message.id);
}

/**
 * Handles feedback submitted from toolbar
 */
function handleFeedbackSubmitted(feedback: ChatMessageFeedback) {
   setMessageFeedback(props.message.id, feedback);
}

/**
 * Handles followup question click
 */
function handleFollowupQuestion(question: string) {
   emit('selectFollowupQuestion', question);
}

/**
 * Handles retry message
 */
function handleRetryMessage(messageId: number) {
   emit('retryMessage', messageId);
}

/** Render this answer's references in the document table beside the chat. */
function handleShowReferences() {
   showReferences(props.message);
}

/** Leave the chat for the search results Luma's suggested filters describe. */
function handleSearchResults() {
   return openSuggestedSearch(props.message);
}
</script>

<template>
   <div>
      <!-- User message bubble -->
      <div v-if="message.role === 'user'" class="w-full flex justify-end">
         <div class="w-auto max-w-[90%] md:max-w-[75%] rounded-3xl bg-[#EFEDED] text-black px-5 py-2">
            {{ message.content }}
         </div>
      </div>

      <!-- AI message bubble -->
      <div v-else class="w-full flex flex-col justify-start mt-4">
         <div class="w-full">
            <!-- Luma's reasoning, streamed as immutable bubbles -->
            <LumaThoughtTrail
               v-if="isLoading"
               :thoughts="thoughts"
               live
               :fallback-text="LOADING_FALLBACK"
               @stop="handleStop"
            />

            <!-- Error state — the trail shows how far Luma got before failing -->
            <template v-else-if="isError">
               <LumaThoughtTrail v-if="hasThoughtTrail" :thoughts="thoughts" />
               <div class="flex flex-col gap-2 py-2">
                  <div class="flex items-center gap-2 text-red-500">
                     <Icon :icon="IconLib.error.circle" class="size-5" />
                     <span class="text-sm">Failed to get response</span>
                     <!-- retry button -->
                     <button
                        class="text-sm text-black/50 hover:text-black transition-all duration-200 underline"
                        @click="handleRetryMessage(message.id)"
                     >
                        Retry
                     </button>
                  </div>
               </div>
            </template>

            <!-- Stopped state — likewise, keep the record of the work done -->
            <template v-else-if="isStopped">
               <LumaThoughtTrail v-if="hasThoughtTrail" :thoughts="thoughts" />
               <div class="flex items-center gap-2 text-gray-500 py-2">
                  <span class="text-sm">Luma stopped thinking</span>
               </div>
            </template>

            <!-- Response content, preceded by the reasoning that produced it -->
            <template v-else-if="message.content">
               <LumaThoughtTrail
                  v-if="hasThoughtTrail"
                  :thoughts="thoughts"
                  class="mb-2"
               />
               <div
                  class="chat-content [overflow-wrap:anywhere] [&_*]:!text-left"
                  v-html="sanitizedContent"
               />
            </template>

            <!-- Toolbar and answer actions — final answers only, so a
                 streaming, stopped or failed turn is never up for feedback. -->
            <div
               v-if="isFinalAnswer"
               class="flex items-start justify-between gap-4 mt-2"
            >
               <ChatMessageToolbar
                  ref="toolbarRef"
                  :message="message"
                  @feedback-submitted="handleFeedbackSubmitted"
               />

               <div class="flex items-center gap-1 shrink-0">
                  <!-- References: what Luma read, shown in the table beside the chat -->
                  <button
                     v-if="hasReferences"
                     class="answer-action"
                     :class="isDocsAdded ? 'answer-action--active' : 'answer-action--idle'"
                     @click="handleShowReferences"
                  >
                     <Icon
                        :icon="isDocsAdded ? IconLib.actions.check : IconLib.actions.list"
                        class="size-5"
                     />
                     <!-- On a small screen the panel is hidden and the click opens a
                          sheet, so the label stays an invitation rather than a state. -->
                     <span>
                        {{ isDocsAdded && !isSmScreen ? 'Showing References' : 'Show References' }}
                        <span class="answer-action__count">({{ referenceCount }})</span>
                     </span>
                  </button>

                  <!-- Suggested search: what the exchange was about, as facets -->
                  <button
                     v-if="hasSearchResults"
                     class="answer-action answer-action--idle"
                     :disabled="isOpeningSearch"
                     @click="handleSearchResults"
                  >
                     <Icon
                        :icon="isOpeningSearch ? IconLib.loading.spinner : 'ph:magnifying-glass'"
                        class="size-5"
                     />
                     <span>
                        Search Results
                        <span class="answer-action__count">({{ searchResultCount?.toLocaleString() }})</span>
                     </span>
                  </button>
               </div>
            </div>

            <!-- Feedback panel (rendered outside flex row for full width) -->
            <ChatMessageFeedbackPanel
               v-if="isFinalAnswer && toolbarRef?.showFeedbackSection"
               v-model="toolbarRef.showFeedbackSection"
               :message="message"
               :original-question="originalQuestion"
               :is-positive="toolbarRef.selectedFeedbackType"
               class="mt-2"
               @feedback-submitted="toolbarRef.handleFeedbackSubmitted"
            />
         </div>

         <!-- Followup questions (controlled by LaunchDarkly feature flag) -->
         <p
            v-if="showFollowUpQuestions && message.followup_questions && message.followup_questions.length > 0"
            class="font-bold my-2"
         >
            You might want to ask
         </p>

         <div
            v-if="showFollowUpQuestions && message.followup_questions && message.followup_questions.length > 0"
            class="w-full flex gap-2 flex-wrap"
         >
            <button
               v-for="(question, index) in message.followup_questions"
               :key="index"
               class="transition-all duration-200 text-sm font-semibold hover:bg-primary/40
               py-2 ps-6 pe-4 rounded-full bg-primary/20 text-left max-w-[95%] flex items-center
               justify-between gap-3"
               @click="handleFollowupQuestion(question)"
            >
               {{ question }}
               <Icon :icon="IconLib.chat.send" class="size-4 flex-shrink-0" />
            </button>
         </div>
      </div>
   </div>
</template>

<style scoped lang="postcss">
/* The answer's two decoration actions, styled as one family. */
.answer-action {
   @apply inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm shrink-0;
   @apply transition-all duration-200 ease-in-out;
   @apply disabled:opacity-50 disabled:cursor-not-allowed;
}

.answer-action--idle {
   @apply text-black/65 hover:bg-black/10 font-medium;
}

.answer-action--active {
   @apply text-primary hover:bg-primary/10 font-semibold;
}

/* The count rides along with the label without competing with it. */
.answer-action__count {
   @apply font-normal tabular-nums opacity-70;
}

:deep(h1) {
   @apply text-2xl font-bold mb-1;
}
:deep(h2) {
   @apply text-xl font-bold mb-1;
}
:deep(h3) {
   @apply text-lg font-bold mb-1;
}
:deep(h4) {
   @apply text-base font-bold mb-0.5;
}
:deep(h5) {
   @apply text-sm font-bold mb-0.5;
}
:deep(h6) {
   @apply text-xs font-bold mb-0.5;
}
:deep(ul) {
   @apply list-disc list-outside ml-5 mb-2 last:mb-0;
}
:deep(ol) {
   @apply list-decimal list-outside ml-5 mb-2 last:mb-0;
}
:deep(li) {
   @apply pl-1 mb-2 last:mb-0;
}
:deep(p) {
   @apply mb-2 last:mb-0;
}
:deep(a) {
   @apply text-primary underline hover:text-primary/80;
}
:deep(strong), :deep(b) {
   @apply font-bold;
}
:deep(em), :deep(i) {
   @apply italic;
}
:deep(table) {
   @apply mb-2 max-w-full border-collapse border-2 border-neutral-dark;
}
:deep(th) {
   @apply border-2 border-neutral-dark bg-neutral-light p-2;
}
:deep(td) {
   @apply border-2 border-neutral-dark p-2;
}
</style>
